"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeAdventureAnalyticsSnapshotUsage = computeAdventureAnalyticsSnapshotUsage;
exports.computeAdventureAnalyticsDisplayNameUsage = computeAdventureAnalyticsDisplayNameUsage;
exports.shouldRefreshAnalyticsSnapshotForPendingOptionSelection = shouldRefreshAnalyticsSnapshotForPendingOptionSelection;
exports.shouldRefreshAnalyticsSnapshotForSceneDisplay = shouldRefreshAnalyticsSnapshotForSceneDisplay;
exports.shouldSyncAnalyticsDisplayNameForPendingOptionSelection = shouldSyncAnalyticsDisplayNameForPendingOptionSelection;
exports.shouldSyncAnalyticsDisplayNameForSceneDisplay = shouldSyncAnalyticsDisplayNameForSceneDisplay;
const analyticsScriptSurface_1 = require("./analyticsScriptSurface");
function appendRichTextElements(elements, parts) {
    if (!elements || elements.length === 0) {
        return;
    }
    for (const el of elements) {
        appendRichTextElement(el, parts);
    }
}
function appendRichTextElement(el, parts) {
    if (!el) {
        return;
    }
    if (typeof el.scriptCode === 'string' && el.scriptCode.length > 0) {
        parts.push(el.scriptCode);
    }
    if (typeof el.condition === 'string' && el.condition.length > 0) {
        parts.push(el.condition);
    }
    if (typeof el.target === 'string' && el.target.length > 0) {
        parts.push(el.target);
    }
    if (typeof el.effects === 'string' && el.effects.length > 0) {
        parts.push(el.effects);
    }
    if (typeof el.selectionTarget === 'string' && el.selectionTarget.length > 0) {
        parts.push(el.selectionTarget);
    }
    if (typeof el.imageId === 'string' && el.imageIdIsScript) {
        parts.push(el.imageId);
    }
    if (typeof el.inventoryOwner === 'string' && el.inventoryOwnerIsScript) {
        parts.push(el.inventoryOwner);
    }
    if (typeof el.locationWithPortraitsLocationTag === 'string' && el.locationWithPortraitsLocationTagIsScript) {
        parts.push(el.locationWithPortraitsLocationTag);
    }
    if (typeof el.syncOptionEffects === 'string' && el.syncOptionEffects.length > 0) {
        parts.push(el.syncOptionEffects);
    }
    const attrs = [
        el.analyticsPosition,
        el.analyticsName,
        el.analyticsValue,
        el.feedbackId,
        el.feedbackType,
        el.feedbackTitle,
        el.feedbackBy,
        el.feedbackAt,
        el.feedbackPreview,
        el.feedbackScene,
        el.feedbackDeployment,
        el.feedbackAdventureDisplay,
    ];
    for (const a of attrs) {
        if (typeof a === 'string' && a.length > 0) {
            parts.push(a);
        }
    }
    if (typeof el.content === 'string' && el.content.length > 0) {
        parts.push(el.content);
    }
    else if (Array.isArray(el.content)) {
        appendRichTextElements(el.content, parts);
    }
    appendRichTextElements(el.children, parts);
    appendRichTextElements(el.elseChildren, parts);
}
function appendRichTextDescription(desc, parts) {
    if (!desc) {
        return;
    }
    if (typeof desc === 'string') {
        parts.push(desc);
        return;
    }
    appendRichTextElements(desc.elements, parts);
}
function appendOptionFields(option, parts) {
    appendRichTextDescription(option.text, parts);
    if (typeof option.target === 'string') {
        parts.push(option.target);
    }
    if (typeof option.condition === 'string') {
        parts.push(option.condition);
    }
    if (typeof option.effect === 'string') {
        parts.push(option.effect);
    }
    appendRichTextDescription(option.waitMessage, parts);
}
function appendSceneFields(scene, parts) {
    if (typeof scene.autoEffect === 'string' && scene.autoEffect.length > 0) {
        parts.push(scene.autoEffect);
    }
    for (const decl of scene.variables ?? []) {
        appendVariableDeclaration(decl, parts);
    }
    for (const section of scene.sections ?? []) {
        appendRichTextDescription(section.description, parts);
        for (const option of section.options ?? []) {
            appendOptionFields(option, parts);
        }
    }
}
function appendVariableDeclaration(decl, parts) {
    if (typeof decl.description === 'string' && decl.description.length > 0) {
        parts.push(decl.description);
    }
    if (decl.defaultValue !== undefined && decl.defaultValue !== null) {
        parts.push(String(decl.defaultValue));
    }
}
function blobTouchesSurface(parts, touchesSurface) {
    const blob = parts.join('\n');
    return touchesSurface(blob);
}
function blobTouchesAnalytics(parts) {
    return blobTouchesSurface(parts, analyticsScriptSurface_1.textUsesAnalyticsScriptGlobals);
}
function blobTouchesDisplayName(parts) {
    return blobTouchesSurface(parts, analyticsScriptSurface_1.textUsesAnalyticsDisplayNameSurface);
}
function appendCharacterFields(character, parts) {
    appendRichTextDescription(character.description, parts);
    for (const decl of character.variables ?? []) {
        appendVariableDeclaration(decl, parts);
    }
}
function appendItemFields(item, parts) {
    appendRichTextDescription(item.description, parts);
    for (const decl of item.variables ?? []) {
        appendVariableDeclaration(decl, parts);
    }
}
function appendLocationFields(location, parts) {
    appendRichTextDescription(location.description, parts);
    for (const decl of location.variables ?? []) {
        appendVariableDeclaration(decl, parts);
    }
}
function appendStatFields(stat, parts) {
    if (typeof stat.description === 'string' && stat.description.length > 0) {
        parts.push(stat.description);
    }
}
function computeAdventureRemoteSurfaceUsage(data, touchesSurface) {
    const sceneIds = new Set();
    const sharedParts = [];
    appendRichTextDescription(data.description, sharedParts);
    if (typeof data.title === 'string' && data.title.length > 0) {
        sharedParts.push(data.title);
    }
    for (const decl of data.variables ?? []) {
        appendVariableDeclaration(decl, sharedParts);
    }
    for (const decl of data.characterVariables ?? []) {
        appendVariableDeclaration(decl, sharedParts);
    }
    for (const character of data.characters ?? []) {
        appendCharacterFields(character, sharedParts);
    }
    for (const item of data.items ?? []) {
        appendItemFields(item, sharedParts);
    }
    for (const location of data.locations ?? []) {
        appendLocationFields(location, sharedParts);
    }
    for (const stat of data.stats ?? []) {
        appendStatFields(stat, sharedParts);
    }
    const sharedContentUses = touchesSurface(sharedParts);
    for (const scene of data.scenes ?? []) {
        const parts = [];
        appendSceneFields(scene, parts);
        if (touchesSurface(parts)) {
            sceneIds.add(scene.id);
        }
    }
    return { sceneIds, sharedContentUses };
}
/**
 * Scans parsed adventure data for references to analytics snapshot script globals.
 * Used to avoid calling `AnalyticsScriptService.refreshSnapshot()` on every player action.
 */
function computeAdventureAnalyticsSnapshotUsage(data) {
    const usage = computeAdventureRemoteSurfaceUsage(data, blobTouchesAnalytics);
    return { sceneIds: usage.sceneIds, sharedContentUsesAnalytics: usage.sharedContentUses };
}
/**
 * Scans parsed adventure data for references to analytics display-name variables or alias helpers.
 * Used to avoid calling `AnalyticsClient.getDisplayName()` on every player action.
 */
function computeAdventureAnalyticsDisplayNameUsage(data) {
    const usage = computeAdventureRemoteSurfaceUsage(data, blobTouchesDisplayName);
    return { sceneIds: usage.sceneIds, sharedContentUsesDisplayName: usage.sharedContentUses };
}
function hasDynamicTargetSyntax(rawTarget) {
    const t = (rawTarget ?? '').toString();
    if (!t) {
        return false;
    }
    if (t.includes("'''")) {
        return true;
    }
    if (t.includes('${')) {
        return true;
    }
    if (t.includes('getVariable(')) {
        return true;
    }
    return false;
}
function conservativeCrossAdventureTarget(rawTarget) {
    const t = (rawTarget ?? '').trim();
    if (!t) {
        return false;
    }
    if (t.startsWith('START_ADVENTURE:') || t.startsWith('START_OR_RESUME_ADVENTURE:') || t.startsWith('JOIN_ADVENTURE:')) {
        return true;
    }
    if (t === 'RETURN_TO_ADVENTURE') {
        return true;
    }
    return false;
}
function tryResolveStaticSceneIdFromTarget(validSceneIds, interpolatedOrRawTarget) {
    const raw = (interpolatedOrRawTarget ?? '').trim();
    if (!raw) {
        return undefined;
    }
    const systemExact = new Set([
        'FINISH',
        'LEAVE',
        'MAIN_MENU',
        'QUIT_ADVENTURE',
        'RETURN_TO_ADVENTURE',
        'LOGGED_IN',
        'SIGN_OUT',
        'DELETE_ACCOUNT',
    ]);
    if (systemExact.has(raw)) {
        return undefined;
    }
    if (raw.startsWith('START_ADVENTURE:') || raw.startsWith('START_OR_RESUME_ADVENTURE:') || raw.startsWith('JOIN_ADVENTURE:')) {
        return undefined;
    }
    let candidate = raw;
    const lower = candidate.toLowerCase();
    if (lower.startsWith('next:')) {
        candidate = candidate.slice('next:'.length).trim();
    }
    if (!candidate) {
        return undefined;
    }
    if (validSceneIds.has(candidate)) {
        return candidate;
    }
    return undefined;
}
function optionFieldsBlobTouchesSurface(option, touchesSurface) {
    const parts = [];
    appendOptionFields(option, parts);
    return touchesSurface(parts);
}
function shouldSyncRemoteSurfaceForPendingOptionSelection(params) {
    if (params.sharedContentUsesSurface) {
        return true;
    }
    if (params.sceneIdsUsingSurface.has(params.currentSceneId)) {
        return true;
    }
    if (optionFieldsBlobTouchesSurface(params.selectedOption, params.touchesSurface)) {
        return true;
    }
    const rawTarget = (params.rawTarget ?? '').toString();
    if (hasDynamicTargetSyntax(rawTarget)) {
        return true;
    }
    if (conservativeCrossAdventureTarget(rawTarget)) {
        return true;
    }
    const interpolated = params.interpolatedNonScriptTarget;
    const dest = tryResolveStaticSceneIdFromTarget(params.validSceneIds, interpolated) ??
        tryResolveStaticSceneIdFromTarget(params.validSceneIds, rawTarget);
    if (dest && params.sceneIdsUsingSurface.has(dest)) {
        return true;
    }
    return false;
}
function shouldSyncRemoteSurfaceForSceneDisplay(params) {
    if (params.sharedContentUsesSurface) {
        return true;
    }
    return params.sceneIdsUsingSurface.has(params.sceneId);
}
function shouldRefreshAnalyticsSnapshotForPendingOptionSelection(params) {
    return shouldSyncRemoteSurfaceForPendingOptionSelection({
        validSceneIds: params.validSceneIds,
        sceneIdsUsingSurface: params.sceneIdsUsingAnalytics,
        sharedContentUsesSurface: params.sharedContentUsesAnalytics,
        currentSceneId: params.currentSceneId,
        selectedOption: params.selectedOption,
        rawTarget: params.rawTarget,
        interpolatedNonScriptTarget: params.interpolatedNonScriptTarget,
        touchesSurface: blobTouchesAnalytics,
    });
}
function shouldRefreshAnalyticsSnapshotForSceneDisplay(params) {
    return shouldSyncRemoteSurfaceForSceneDisplay({
        sharedContentUsesSurface: params.sharedContentUsesAnalytics,
        sceneIdsUsingSurface: params.sceneIdsUsingAnalytics,
        sceneId: params.sceneId,
    });
}
function shouldSyncAnalyticsDisplayNameForPendingOptionSelection(params) {
    return shouldSyncRemoteSurfaceForPendingOptionSelection({
        validSceneIds: params.validSceneIds,
        sceneIdsUsingSurface: params.sceneIdsUsingDisplayName,
        sharedContentUsesSurface: params.sharedContentUsesDisplayName,
        currentSceneId: params.currentSceneId,
        selectedOption: params.selectedOption,
        rawTarget: params.rawTarget,
        interpolatedNonScriptTarget: params.interpolatedNonScriptTarget,
        touchesSurface: blobTouchesDisplayName,
    });
}
function shouldSyncAnalyticsDisplayNameForSceneDisplay(params) {
    return shouldSyncRemoteSurfaceForSceneDisplay({
        sharedContentUsesSurface: params.sharedContentUsesDisplayName,
        sceneIdsUsingSurface: params.sceneIdsUsingDisplayName,
        sceneId: params.sceneId,
    });
}
//# sourceMappingURL=analyticsScriptSnapshotPolicy.js.map